import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { initiatePayment, checkPaymentStatus } from './bookingService';

interface PaymentFormProps {
  clinicId: string;
  serviceId: string;
  practitionerId: string;
  amount: number;
  patientEmail: string;
  patientName: string;
  // Optional appointment details for automatic creation
  appointmentDetails?: {
    starts_at?: string;
    ends_at?: string;
    practitioner_id?: string;
    patient_first_name?: string;
    patient_last_name?: string;
    patient_phone?: string;
    notes?: string;
  };
  returnUrl?: string;
  onSuccess: (paymentData: { 
    status: 'complete', 
    transactionId: string,
    accessCode: string 
  }) => void;
  onCancel: () => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  clinicId,
  serviceId,
  practitionerId,
  amount,
  patientEmail,
  patientName,
  appointmentDetails,
  returnUrl,
  onSuccess,
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [accessCode, setAccessCode] = useState<string | null>(null);
  const [paymentWindow, setPaymentWindow] = useState<Window | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Listen for payment complete/cancel messages from popup window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      console.log('Received message from eWay payment window:', event.data);
      
      // Check for message format from the server's payment-complete route
      if ((event.data?.type === 'paymentComplete' || event.data?.type === 'payment_complete') && 
          (event.data?.accessCode || event.data?.accessCode)) {
        console.log('eWay payment complete message received:', event.data);
        const paymentAccessCode = event.data.accessCode;
        
        // If we already have transaction ID in the message, use it directly
        if (event.data.transactionId && event.data.status === 'complete') {
          console.log('Transaction data received in message, skipping status check');
          onSuccess({
            status: 'complete',
            transactionId: event.data.transactionId,
            accessCode: paymentAccessCode
          });
          return;
        }
        
        // Otherwise check payment status with the server to get transaction ID
        checkPaymentStatus(paymentAccessCode)
          .then(statusResponse => {
            console.log('Payment status response:', statusResponse);
            
            if (statusResponse.status === 'complete' && statusResponse.transactionId) {
              // Mark payment as successful with actual transaction ID
              onSuccess({
                status: 'complete',
                transactionId: statusResponse.transactionId,
                accessCode: paymentAccessCode
              });
            } else {
              // Handle pending or failed status
              console.error('Payment not completed:', statusResponse);
              onCancel();
            }
          })
          .catch(err => {
            console.error('Error checking payment status:', err);
            onCancel();
          });
      } else if (event.data === 'PAYMENT_COMPLETE' && accessCode) {
        // Old format - for backward compatibility
        console.log('eWay payment complete message received (old format)');
        checkPaymentStatus(accessCode)
          .then(statusResponse => {
            if (statusResponse.status === 'complete' && statusResponse.transactionId) {
              onSuccess({
                status: 'complete',
                transactionId: statusResponse.transactionId,
                accessCode: accessCode
              });
            } else {
              console.error('Payment not completed:', statusResponse);
              onCancel();
            }
          })
          .catch(err => {
            console.error('Error checking payment status:', err);
            onCancel();
          });
      } else if (event.data === 'PAYMENT_CANCELLED') {
        console.log('eWay payment cancelled message received');
        onCancel();
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [accessCode, onSuccess, onCancel]);

  // Initiate payment on component mount
  useEffect(() => {
    const initiatePaymentProcess = async () => {
      try {
        setLoading(true);
        setError(null);

        // Validate required fields
        if (!clinicId || !serviceId || !amount || !patientEmail || !patientName) {
          throw new Error('Missing required payment information');
        }

        // Log the payment request details
        console.log('Initiating eWay payment with details:', {
          clinic_id: clinicId,
          service_id: serviceId,
          amount,
          patient: patientEmail,
          appointment: appointmentDetails ? 'provided' : 'not provided'
        });

        // Create the payment data object
        const paymentRequest = {
          clinic_id: clinicId,
          appointment_type_id: serviceId,
          practitioner_id: practitionerId,
          amount,
          patient_email: patientEmail,
          patient_name: patientName,
          // Add appointment details if available
          ...appointmentDetails,
          // Include returnUrl if available
          returnUrl
        };
        
        // Log full payment details for debugging
        console.log('Initiating payment with appointment details:', {
          ...paymentRequest,
          practitioner_id: practitionerId,
          starts_at: appointmentDetails?.starts_at ? new Date(appointmentDetails.starts_at).toLocaleString() : 'not provided',
          ends_at: appointmentDetails?.ends_at ? new Date(appointmentDetails.ends_at).toLocaleString() : 'not provided'
        });
        
        const response = await initiatePayment(paymentRequest);

        console.log('eWay payment initiated:', response);
        
        if (!response.accessCode || !response.formUrl) {
          throw new Error('Invalid payment response from server');
        }
        
        setAccessCode(response.accessCode);
        setPaymentUrl(response.formUrl);
      } catch (err: any) {
        console.error('eWay payment initiation failed:', err);
        setError(err.message || 'Failed to initiate payment with eWay');
      } finally {
        setLoading(false);
      }
    };

    initiatePaymentProcess();
  }, [clinicId, serviceId, amount, patientEmail, patientName, appointmentDetails]);

  // Open payment window when payment URL is available
  const handleStartPayment = () => {
    if (!paymentUrl) {
      setError('Payment URL is not available. Please try again later.');
      return;
    }

    try {
      console.log('Opening payment window with URL:', paymentUrl);
      
      // Direct full-page redirect instead of popup window
      // This ensures proper redirect handling by eWay
      window.location.href = paymentUrl;
      
      // Set status for UI feedback
      setCheckingStatus(true);
    } catch (err) {
      console.error('Error redirecting to payment page:', err);
      setError('Failed to open payment page. Please try again.');
    }
  };

  // We're not using polling to check status anymore as we're using window.postMessage
  // when the payment completes. This is handled by the useEffect above.

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h5" gutterBottom>
        Payment
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={onCancel}>
              Go Back
            </Button>
          </Box>
        </Alert>
      ) : (
        <Box>
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Payment Summary
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Amount:</Typography>
              <Typography fontWeight="bold">${typeof amount === 'number' ? amount.toFixed(2) : amount || '0.00'}</Typography>
            </Box>
          </Paper>

          {checkingStatus ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography>
                Please complete payment in the opened window. Do not close this page.
              </Typography>
            </Box>
          ) : paymentUrl ? (
            <Box sx={{ textAlign: 'center' }}>
              <Button 
                variant="contained" 
                color="primary" 
                size="large"
                onClick={handleStartPayment}
                sx={{ mb: 2 }}
              >
                Proceed to Payment
              </Button>
              <Typography variant="body2" color="text.secondary">
                You will be redirected to our secure payment provider, eWay
              </Typography>
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default PaymentForm;